import { fabric } from 'fabric';
import { AttachmentPoint, ConnectorEnd } from '../../types';
import type WhiteboardController from '../../controllers/WhiteboardController';
import type ShapeBase from '../../shapes/ShapeBase';
import Shape from '../../objects/Shape';
import { type IPoint } from 'fabric/fabric-impl';
import { hasEmbeddedTextBehaviors, isShape } from '../../objects/guards';
import Connector from '../../objects/Connector';
import { getTextColorFromSelection } from '../../utils/getTextColor';

export const CONNECTOR_OFFSET = 75;
export const NOTE_OFFSET = 45;

/**
 * Given a shape, create a duplicate of it to the right. If the original shape
 * had an incoming connector, connect the new shape with the old one via a
 * connector.
 */
export function createSequentialShape(
  fabricObject: fabric.Object,
  whiteboardController: WhiteboardController
) {
  if (isShape(fabricObject)) {
    fabricObject.endTextEditing();
  }

  const newObject = buildShapeAndConnector(
    fabricObject,
    AttachmentPoint.Right,
    AttachmentPoint.Left
  );

  whiteboardController.canvas.setActiveObject(newObject);

  setTimeout(() => {
    void newObject.startTextEditing(true);
  }, 100);
}

export function buildShapeAndConnector(
  sourceObj: fabric.Object,
  sourceAttachmentPoint: AttachmentPoint,
  targetAttachmentPoint: AttachmentPoint,
  normal: IPoint = { x: 1, y: 0 }
) {
  if (!isShape(sourceObj)) {
    throw new Error('Can only build shapes from other shapes');
  }

  const newShape = buildShape(sourceObj, sourceAttachmentPoint, normal);
  const controller = sourceObj.controller;

  // Mark the new shape as newly created to enable Tab key sequential creation
  newShape.isNewlyCreated = true;

  controller.addToCanvas(newShape);
  const connectorOptions = {
    controller,
    ...controller.defaultsManager.for('connector'),
  };

  if (sourceObj.angle) {
    // Circular import prevents us importing ConnectorRouting.ROUTING_BEZIER const
    connectorOptions.lineRouting = 10;
  }

  // Sticky notes do not get connectors when just clicking to create.
  if (isShape(newShape) && newShape.shapeName !== 'note') {
    const connector = Connector.create(
      [
        { x: 0, y: 0 },
        { x: 1, y: 1 },
      ],
      connectorOptions
    );

    // Attach the connector
    sourceObj.attachConnector(
      connector,
      ConnectorEnd.CONNECTOR_START,
      sourceAttachmentPoint
    );
    newShape.attachConnector(
      connector,
      ConnectorEnd.CONNECTOR_END,
      targetAttachmentPoint
    );
    connector.constrainConnector({});
  }

  return newShape;
}

export function buildShape(
  startObj: Shape<ShapeBase>,
  attachmentPoint: AttachmentPoint,
  normal: IPoint = { x: 1, y: 0 }
) {
  // See if the starting object already has a connector from this location.
  // If so, offset the next object a bit so they don't overlap.
  const existingAttachments =
    startObj.connectorsForAttachmentPoint(attachmentPoint);
  const additionalOffsetFactor = 20 * existingAttachments.length;

  const newShapeName = startObj.shapeName;
  let textShapeOptions = {};

  if (hasEmbeddedTextBehaviors(startObj)) {
    textShapeOptions = {
      textColor: getTextColorFromSelection(startObj),
      bold: startObj.selectionIsEntirely('bold'),
      italic: startObj.selectionIsEntirely('italic'),
      code: startObj.selectionIsEntirely('code'),
      strikethrough: startObj.selectionIsEntirely('strikethrough'),
      underline: startObj.selectionIsEntirely('underline'),
    };
  }

  const newShapeOptions = {
    width: startObj.width,
    height: startObj.height,
    scaleX: startObj.scaleX,
    scaleY: startObj.scaleY,
    angle: startObj.angle,
    fill: startObj.fill,
    opacity: startObj.opacity,
    stroke: startObj.stroke,
    strokeWidth: startObj.strokeWidth,
    strokeType: startObj.strokeType,
    borderOpacity: startObj.borderOpacity,
    fontSize: startObj.fontSize,
    textAlign: startObj.textAlign,
    textOpacity: startObj.textOpacity,
    textColor: startObj.textColor,
    variant: startObj.variant,
    ...textShapeOptions,

    // Special handling for some shapes.
    ...(() => {
      switch (newShapeName) {
        case 'image':
        case 'video':
          return {
            attachmentId: startObj.attachmentId,
            contentType: startObj.contentType,
          };

        case 'record':
          return {
            recordId: startObj.recordId,
            recordType: startObj.recordType,
          };

        case 'smartRecord':
          return {
            recordType: startObj.recordType,
          };

        case 'integrationRecord':
          return {
            integrationRecord: startObj.integrationRecord,
          };

        case 'icon':
        case 'spriteIcon':
          return {
            iconName: startObj.iconName,
            strokeWidth: 0,
          };

        case 'customPath':
          return {
            shapePath: startObj.shapePath,
          };
      }
    })(),
  };

  const newHalfWidth = (newShapeOptions.width * newShapeOptions.scaleX) / 2;
  const newHalfHeight = (newShapeOptions.height * newShapeOptions.scaleY) / 2;
  const startHalfWidth = (startObj.width * startObj.scaleX) / 2;
  const startHalfHeight = (startObj.height * startObj.scaleY) / 2;
  const shapeSpacing = newShapeName === 'note' ? NOTE_OFFSET : CONNECTOR_OFFSET;

  const position = {
    x:
      startHalfWidth +
      normal.x * (startHalfWidth + shapeSpacing + newHalfWidth) +
      additionalOffsetFactor -
      newHalfWidth,
    y:
      startHalfHeight +
      normal.y * (startHalfHeight + shapeSpacing + newHalfHeight) +
      additionalOffsetFactor -
      newHalfHeight,
  };
  const angle = fabric.util.degreesToRadians(startObj.angle ?? 0);
  const offset = fabric.util.rotateVector(position, angle);

  return new Shape({
    shapeName: newShapeName,
    authorId: startObj.controller.state.userId,
    controller: startObj.controller,
    canvas: startObj.controller.canvas,

    left: startObj.left + offset.x,
    top: startObj.top + offset.y,

    ...newShapeOptions,
  });
}
